/**
 * Aligns with news-portal-backend news.PodcastEpisode JSON for reader apps.
 * Stored inside flock podcast `content` with a prefix. `script` is flock-only for the script body.
 *
 * @see news-portal-backend/internal/domain/news/podcast.go — PodcastEpisode
 */

import type { WikiConcept } from '../types/types';
import type { PortalContentBlock } from './portalDraftEnvelope';

export const PORTAL_PODCAST_MAGIC = 'portal-podcast-episode:v1\n';

export type PortalPodcastEpisodePayload = {
  id: string;
  title: string;
  show: string;
  duration: string;
  progress: number;
  /** Legacy cover / small art; kept in sync with thumbnail when set from Library */
  image: string;
  date: string;
  /** Square/listing art (Library editor — same URL flow as event banner) */
  thumbnail_image?: string;
  /** Wide hero art */
  banner_image?: string;
  /** Article-aligned taxonomy (replaces free-text vertical in the editor) */
  category?: string;
  vertical_id?: string;
  vertical?: string;
  /** Flock script body; Library maps to the same text as `show` when saving */
  script?: string;
  /** Editor.js blocks for the script. `show` and `script` stay the plain text. */
  content_blocks?: PortalContentBlock[];
  /** Library-only flag stored in serialized draft content */
  ready_for_publishing?: boolean;
  /** Library linked entities (same idea as article draft linked_wiki_entities). */
  linked_wiki_entities?: WikiConcept[];
};

export function defaultPodcastEpisodePayload(podcastId: string): PortalPodcastEpisodePayload {
  const d = new Date().toISOString().slice(0, 10);
  return {
    id: podcastId,
    title: 'New podcast draft',
    show: '',
    duration: '0:00',
    progress: 0,
    image: '',
    date: d,
    script: '',
    thumbnail_image: '',
    banner_image: '',
    category: 'News',
  };
}

export function serializePodcastEpisodeDraft(p: PortalPodcastEpisodePayload): string {
  return PORTAL_PODCAST_MAGIC + JSON.stringify(p);
}

function normalizeEpisodePayload(p: PortalPodcastEpisodePayload): PortalPodcastEpisodePayload {
  const category =
    (p.category || '').trim() ||
    (p.vertical || '').trim() ||
    'News';
  const thumb = (p.thumbnail_image || '').trim() || (p.image || '').trim();
  const banner = (p.banner_image || '').trim();
  return {
    ...p,
    category,
    thumbnail_image: thumb,
    banner_image: banner,
    image: thumb || p.image || '',
    vertical: category,
    vertical_id: p.vertical_id || '',
  };
}

export function tryParsePodcastEpisodeDraft(raw: string): PortalPodcastEpisodePayload | null {
  if (!raw || typeof raw !== 'string') return null;
  if (!raw.startsWith(PORTAL_PODCAST_MAGIC)) return null;
  try {
    const p = JSON.parse(raw.slice(PORTAL_PODCAST_MAGIC.length)) as PortalPodcastEpisodePayload;
    return normalizeEpisodePayload(p);
  } catch {
    return null;
  }
}

/** Legacy flock podcasts: plain text script only. */
export function parseOrDefaultPodcastEpisode(
  rawContent: string,
  podcastId: string,
  titleFallback: string,
  showFallback: string
): PortalPodcastEpisodePayload {
  const parsed = tryParsePodcastEpisodeDraft(rawContent);
  if (parsed) return parsed;
  const script = rawContent || '';
  const summaryTrim = (showFallback || '').trim();
  // Legacy plain-text `content`: API often stores a short excerpt in `description`/`summary` and
  // the full script in `content`. Prefer the longer text so the editor shows the whole episode.
  const show = script.trim().length > summaryTrim.length ? script : summaryTrim || script;
  return normalizeEpisodePayload({
    ...defaultPodcastEpisodePayload(podcastId),
    title: titleFallback || 'Podcast',
    show,
    script,
  });
}

/** Serialize for API: keep legacy `vertical` aligned with `category`, script mirrors show. */
export function serializePodcastEpisodeDraftForSave(p: PortalPodcastEpisodePayload): string {
  const cat = (p.category || p.vertical || 'News').trim() || 'News';
  const show = (p.show ?? '').trim();
  const script = (p.script ?? '').trim() || show;
  const thumb = (p.thumbnail_image || '').trim() || (p.image || '').trim();
  const merged: PortalPodcastEpisodePayload = {
    ...p,
    category: cat,
    vertical: cat,
    vertical_id: '',
    show,
    script,
    thumbnail_image: thumb,
    image: thumb,
  };
  return serializePodcastEpisodeDraft(merged);
}
